import { useState, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Eye, X } from 'lucide-react';
import { PeriodSelector, validatePeriodMax12Months, type PeriodValue } from '@/shared/components/PeriodSelector.tsx';
import { DownloadButton } from '@/shared/components/DownloadButton.tsx';
import { ReportTable } from '@/shared/components/ReportTable.tsx';
import { useProbeSummary } from '../hooks/useReports.ts';
import { TenantCombobox } from '@/shared/components/TenantCombobox.tsx';
import type { ProbeSummaryParams } from '../api/reports.api.ts';
import type { ProbeSummaryRow } from '@/shared/types/report.ts';

const col = createColumnHelper<ProbeSummaryRow>();

interface TabProps { availableMonths?: string[] }

export function ProbeSummaryTab({ availableMonths }: TabProps) {
  const MONTH_NOW = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [period, setPeriod] = useState<PeriodValue>({ mode: 'month', month: MONTH_NOW });
  const [tenantFilter, setTenantFilter] = useState('');
  const [queryParams, setQueryParams] = useState<ProbeSummaryParams | null>(null);
  const [periodError, setPeriodError] = useState('');
  const [tenantError, setTenantError] = useState('');

  const { data, isLoading } = useProbeSummary(queryParams);

  function handleVisualize() {
    setPeriodError('');
    if (!tenantFilter) { setTenantError('Selecione um tenant'); return; }
    setTenantError('');

    if (period.mode === 'month') {
      if (!period.month) { setPeriodError('Selecione um mes'); return; }
      const [y, m] = period.month.split('-').map(Number);
      const from = new Date(y, m - 1, 1).toISOString();
      const to = new Date(y, m, 0, 23, 59, 59).toISOString();
      setQueryParams({ from, to, tenantId: tenantFilter });
    } else {
      const err = validatePeriodMax12Months(period);
      if (err) { setPeriodError(err); return; }
      if (!period.from || !period.to) { setPeriodError('Selecione o intervalo completo'); return; }
      setQueryParams({
        from: new Date(period.from).toISOString(),
        to: new Date(period.to).toISOString(),
        tenantId: tenantFilter,
      });
    }
  }

  const downloadParams: Record<string, string | undefined> = queryParams
    ? { from: queryParams.from, to: queryParams.to, tenantId: queryParams.tenantId }
    : {};

  const columns = useMemo(
    () => [
      col.accessor('tenantNome', { header: 'Tenant' }),
      col.accessor('totalChecks', {
        header: 'Total Checks',
        cell: (info) => <span className="font-mono">{info.getValue()}</span>,
      }),
      col.accessor('pctUP', {
        header: '% UP',
        cell: (info) => (
          <span className="font-mono" style={{ color: 'var(--color-status-up)' }}>
            {info.getValue()}%
          </span>
        ),
      }),
      col.accessor('pctDOWN', {
        header: '% DOWN',
        cell: (info) => (
          <span className="font-mono" style={{ color: 'var(--color-status-down)' }}>
            {info.getValue()}%
          </span>
        ),
      }),
      col.accessor('avgResponseTimeMs', {
        header: 'Latência Média (ms)',
        cell: (info) => <span className="font-mono">{info.getValue()}</span>,
      }),
      col.accessor('maxResponseTimeMs', {
        header: 'Latência Máx (ms)',
        cell: (info) => <span className="font-mono">{info.getValue()}</span>,
      }),
      col.accessor('top3Erros', {
        header: 'Top 3 Erros',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return <span className="text-gray-400">—</span>;
          const erros = val.split('; ').filter(Boolean);
          return (
            <div className="max-w-[350px] space-y-1">
              {erros.map((erro, i) => (
                <div key={i} className="text-xs text-red-600 dark:text-red-400">
                  {erro}
                </div>
              ))}
            </div>
          );
        },
      }),
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 pb-2">
        <div className="relative pb-4">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Período</label>
          <PeriodSelector value={period} onChange={setPeriod} modes={['month', 'range']} availableMonths={availableMonths} error={periodError} />
        </div>
        <div className="relative pb-4 min-w-[220px]">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
            Tenant <span className="text-red-400">*</span>
          </label>
          <TenantCombobox
            value={tenantFilter}
            onChange={(id) => { setTenantFilter(id); setTenantError(''); }}
            placeholder="Selecione..."
            required
          />
          {tenantError && <p className="absolute -bottom-0.5 left-0 text-xs text-red-500">{tenantError}</p>}
        </div>
        <div className="pb-4">
          <button
            onClick={handleVisualize}
            disabled={isLoading}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Eye size={14} />
            {isLoading ? 'Carregando...' : 'Visualizar'}
          </button>
        </div>
        <div className="pb-4">
          <DownloadButton
            url="/admin/reports/probe-summary/pdf"
            params={downloadParams}
            filename="R004-probe-resumido.pdf"
            label="Exportar PDF"
            disabled={!queryParams}
          />
        </div>
        <div className="pb-4">
          <DownloadButton
            url="/admin/reports/probe-summary/csv"
            params={downloadParams}
            filename="R004-probe-resumido.csv"
            label="Exportar CSV"
            disabled={!queryParams}
          />
        </div>
        {(tenantFilter || queryParams) && (
          <div className="pb-4">
            <button
              onClick={() => {
                setTenantFilter('');
                setPeriod({ mode: 'month', month: MONTH_NOW });
                setQueryParams(null);
                setPeriodError('');
                setTenantError('');
              }}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X size={14} />
              Limpar
            </button>
          </div>
        )}
      </div>

      {queryParams && <ReportTable data={data ?? []} columns={columns} />}
    </div>
  );
}
